<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026
  // choose picture from "hand me.." + spoken sentence
  // correct pictures moves to hand in top panel
  // red frame after error
  // goes to step 5 after error
  // OBS: only asks for 3 of 4 pictures
  // last picture saved in store to use in step 5: spelling

  import { crossfade } from "svelte/transition";
  import { flip } from "svelte/animate";

  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import type { VerbAndObject } from "../types";
  import { HelpLevel } from "../classes";
  import { afterCorrectWaitSecs, firstWaitSecs } from "../constants";

  import { Icons } from "../utilities/icons";
  import { shuffleArray } from "../utilities/shuffleArray";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { targetVerbAndObjects } from "../globals/targets";
  import { route } from "../stores/route.svelte";
  import { progress } from "../stores/progress.svelte";
  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import {
    currentSentenceNumberForSteps,
    targetsForSteps,
  } from "../stores/targets-step567.svelte"; // last picture shown should be first in step 5

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // TYPES --------------------------------------------------

  // help levels for help from pressing help button
  enum RequestedHelpLevel {
    GeneralInstruction = "generalInstruction",
    VibrationHelp = "vibrationHelp",
    FingerPointingHelp = "fingerPointingHelp",
    GiveAnswerHelp = "giveAnswerHelp",
    ShowCorrectAnswer = "showCorrectAnswer",
  }

  // help levels for automatic help after chosen delay of no response
  enum TimedHelpLevel {
    GeneralInstruction = "generalInstruction",
    VibrationHelp = "vibrationHelp",
    FingerPointingHelp = "fingerPointingHelp",
    GiveAnswerHelp = "giveAnswerHelp",
    ShowCorrectAnswer = "showCorrectAnswer",
  }

  // CONSTANTS --------------------------------------------

  // neded for Svelte animation
  const [send, receive] = crossfade({});

  const numberOfHelpLevels = 6;
  // const as numberOfStages = 4;

  // STATE ------------------------------------------------

  let currentState: State = $state(State.WaitToStart); // currentState for task solution for each item/picture
  let stage: 0 | 1 | 2 = $state(0); // 0-2, one stage for each sentence presented
  let targets = $state([0, 1, 2, 3]); // is randomized at init
  let clickedItem = $state(-1); // -1: no image clicked, otherwise index of clicked image

  // arrays for send and received sentences
  //  needed for animation to work
  let sendItems: Array<Array<VerbAndObject>> = $state([[]]);
  let receiveItems: Array<VerbAndObject> = $state([]);

  let targetFound = $state([false, false, false, false]);

  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(false); // // help not active for some seconds after activted last time

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;
  let playNextAudioTimer: ReturnType<typeof setTimeout>;

  // help both after a set time and requested - levels interact!
  let helpLevel = new HelpLevel(numberOfHelpLevels);

  let imgWiggle = $state([false, false, false, false]);
  let fingerPointing = $state([false, false, false, false]);

  // for results:
  let gotHelpForItem = $state(false);
  let hasErrorForItem = $state(false);

  // FOR RESEARCH ONLY
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let responseDetails: Array<ResponseDetail> = [];
  // let attempt = 1;

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step4 currentState:", currentState);
  });
  $effect(() => {
    logger("Step4 stage:", stage);
  });
  $effect(() => {
    logger("Step4 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  const countHelp = () => {
    switch (stage) {
      case 0:
        helpCount.value.comprehension.item41++;
        break;
      case 1:
        helpCount.value.comprehension.item42++;
        break;
      case 2:
        helpCount.value.comprehension.item43++;
        break;
      default:
        console.error("Step 4, countHelp: stage out of range: " + stage);
    }
  };

  // show repeat button a defined time after showing task

  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarchies (timed and requested help) ---

  const startHelpTimerAndWaitForResponse = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      timedHelp();
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  // showing help according to 2 level hierarchies (timed and requested help) ---

  // general instruction is repeated
  const generalInstructionHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(
      audioTag,
      DaDK.Step4Intro,
      afterGeneralInstructionHelp,
    );
  };

  const afterGeneralInstructionHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  const tryAgainSentenceHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.TryAgain, repeatSentence);
  };

  const repeatSentence = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  const giveMeSentenceHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.HandMe, afterGiveMeSentenceHelp);
  };

  const afterGiveMeSentenceHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // image vibrates and get glowing shadow, sentence sound repeated
  const vibrationHelp = () => {
    // set leve for both types of help
    helpLevel.setNextLevel();
    imgWiggle[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // finger slids up to correct sentence, sentence sound repeated
  const fingerPointingHelp = () => {
    // After finger pointing help give correct answer and go on
    helpLevel.setNextLevel();
    fingerPointing[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // last help level is to provide correct answer
  const giveAnswerHelp = () => {
    progress.value += 1;
    countHelp();
    currentState = State.Correct;
    helpLevel.setNextLevel();
    setVariablesForCorrectResponse();
    // say correct sentence
    receiveItems.unshift(sendItems[targets[stage]][0]); // OBS: putting at start of array!
    sendItems[targets[stage]].pop();
    targetFound[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentenceOK,
      afterGiveAnswerHelp,
    );
  };
  const afterGiveAnswerHelp = () => {
    // next picture or task
    helpTimer = setTimeout(() => {
      requestedHelpPaused = true;
      afterCorrectResponseOrLastHelpLevel();
    }, studentUserData.value.settings.helpDelaySecs * 500); // half of help delay
  };

  // give help after clicking help button according to help level
  const afterErrorHelp = () => {
    currentState = State.GivingHelp;
    logger("Step4 afterErrorHelp");
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    switch (helpLevel.level) {
      case 1:
        tryAgainSentenceHelp();
        break;
      case 2:
        giveMeSentenceHelp();
        break;
      case 3:
        giveMeSentenceHelp();
        break;
      case 4:
        vibrationHelp();
        break;
      case 5:
        fingerPointingHelp();
        break;
      case 6:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "error in errorHelp() switch, helpLevel: ",
          helpLevel.level,
          " stage: ",
          stage,
        );
    }
  };

  // give help after a duration according to help level
  const timedHelp = () => {
    currentState = State.GivingHelp;
    logger("Step4 timedHelp");
    countHelp();
    // numberOfTimedHelp++;  // FOR RESEARCH ONLY
    gotHelpForItem = true;
    requestedHelpPaused = true;
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    switch (helpLevel.level) {
      case 1:
        generalInstructionHelp();
        break;
      case 2:
        giveMeSentenceHelp();
        break;
      case 3:
        giveMeSentenceHelp();
        break;
      case 4:
        vibrationHelp();
        break;
      case 5:
        fingerPointingHelp();
        break;
      case 6:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "error in timedHelp() switch, helpLevel: ",
          helpLevel.level,
          " stage: ",
          stage,
        );
    }
  };

  const requestedHelp = () => {
    if (currentState !== State.WaitForResponse) {
      return;
    }
    currentState = State.RepeatingTask;
    clearTimeout(helpTimer);
    logger("Step4 requestedHelp");
    countHelp();
    // numberOfRequestedHelp++;  // FOR RESEARCH ONLY
    requestedHelpPaused = true;
    playSoundWithAudioTag(audioTag, DaDK.Step4Help, afterRequestedHelp);
  };

  const afterRequestedHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // presentation of task and progression ------------------------------------

  // pause before sentence is played
  const waitAndStart = (): void => {
    playNextAudioTimer = setTimeout(() => {
      playIntro();
    }, firstWaitSecs);
  };

  const playIntro = (): void => {
    currentState = State.PlayIntro;
    playSoundWithAudioTag(audioTag, DaDK.Step4Intro, waitAfterIntro);
  };

  const waitAfterIntro = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    playNextAudioTimer = setTimeout(() => {
      askForPicture1();
    }, firstWaitSecs);
  };

  const askForPicture1 = (): void => {
    currentState = State.PresentTask;
    playSoundWithAudioTag(audioTag, DaDK.HandMe, askForPicture2);
  };

  const askForPicture2 = (): void => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      WaitForResponse,
    );
  };

  const WaitForResponse = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startRepeatButtonTimer();
    startHelpTimerAndWaitForResponse();
    // initializeResultVariables();  // FOR RESEARCH ONLY
  };

  // common reset for chosen correct response and given as last stage of help
  const setVariablesForCorrectResponse = () => {
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.comprehensionCorrectFirstTry =
        result.value.comprehensionCorrectFirstTry + 1;
    }
    progress.value += 1;
    progress;
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    allowRepetition = false;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    // attempt = 1; // FOR RESEARCH ONLY
  };

  // correct ----

  const correctResponse = (): void => {
    receiveItems.unshift(sendItems[clickedItem][0]); // OBS: putting at start of array!
    sendItems[clickedItem].pop();
    playCorrectRandomWithAudioTag(
      audioTag,
      afterCorrectResponseOrLastHelpLevel,
    );
  };

  const afterCorrectResponseOrLastHelpLevel = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    tasksCompleted.value = tasksCompleted.value + 1;
    // only ask for 3 of 4 pictures - last pictures used in next task
    if (stage < 2) {
      stage++;
      currentState = State.PresentTask;
      helpLevel.reset();
      gotHelpForItem = false;
      hasErrorForItem = false;
      waitAfterIntro();
    } else {
      setCompletedBackgroundColor(true);
      currentState = State.WaitAfterEnd;
      // result.value.responseDetails.push(...responseDetails); // for research
      playSoundWithAudioTag(audioTag, DaDK.Step4LeaveLastPicture, gotoNextStep);
    }
  };

  const gotoNextStep = () => {
    // result.value.responseDetails.push(...responseDetails);
    result.value.lastStepCompleted = 4;
    setTimeout(() => {
      route.current = Pages.Step5;
    }, afterCorrectWaitSecs);
  };

  // error -----

  const afterError = (): void => {
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    hasErrorForItem = true;
    allowRepetition = false;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    afterErrorHelp();
  };

  // button event handling ------

  // evaluate image clicked
  const imgClicked = (index: number): void => {
    logger("Step4 clickedItem:", clickedItem);
    clearTimeout(playNextAudioTimer);
    clickedItem = index;
    if (index === targets[stage]) {
      // saveResultVariablesWithCorrect(true);
      currentState = State.Correct;
      targetFound[index] = true;
      setVariablesForCorrectResponse();
      correctResponse();
    } else {
      // saveResultVariablesWithCorrect(false);  // FOR RESEARCH ONLY
      // attempt++;  // FOR RESEARCH ONLY
      currentState = State.Error;
      afterError();
    }
  };

  // when repeat button is pressed
  const repeatSound = (): void => {
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      allowRepetition = true;
      startHelpTimerAndWaitForResponse();
    } else {
      pauseStartTime = Date.now();
      clearTimeout(playNextAudioTimer);
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    // stopSoundWithAudioTag(audioTag);
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    currentState === State.WaitAfterEnd;
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimerAndWaitForResponse();
  };

  // FOR RESEARCH ONLY
  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production
  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 4,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem: targetVerbAndObjects.value[targets[stage]].sentence,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(playNextAudioTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);
  // randomize order pictures are seleted for targets
  shuffleArray(targets);

  // prepare for steps 5 - 6 - 7
  // has to start with last sentence chosen on this steps
  // steps 5 - 6 - 7 as a unit has one sentence a time and runs through 4 times
  // count run through steps 5 to 7
  currentSentenceNumberForSteps.value = 0;
  // order of centences for steps 5 - 6 - 7 (need to randomize 3 last sentences?):
  targetsForSteps.value = [
    targets[3],
    targets[0],
    targets[2],
    targets[1],
    targets[1],
  ]; // one extra to avoid undefined at last sentence step 7

  // array for send items used for animation
  for (let i = 0; i < targets.length; i++) {
    sendItems.push([]);
    sendItems[i].push(targetVerbAndObjects.value[i]);
  }
  waitAndStart();
</script>

<!-- HTML ===================================================== -->

<main data-page="step4">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <div
    data-testid="index"
    data-index={targets[stage]}
    style="display: none;"
  ></div>
  <!-- top panel, help and repeat buttons
  pictures animated to this position after correct -->
  <div class="top-panel">
    <div class="buttons">
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          icon={Icons.TrafficLightRed}
          onclick={exitButtonClicked}
        />
      </span>
      <RoundButton
        onclick={pauseClicked}
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div class="target-div">
      <!-- animated recipient of chosen pictures -->
      {#each receiveItems as item, i (item)}
        <div
          style="background-image: url('{Path.ImgBg}{item.image}{Extension.ImgBg}'); background-image: image-set('{Path.ImgBg}{item.image}{Extension.WebpBg}'); background-size: cover;"
          class="target-img-div target-{i}"
          animate:flip
          in:receive|global={{ key: item }}
        >
          <picture>
            <source
              type="image/webp"
              srcset={encodeURI(Path.Img + item.image + Extension.Webp)}
              draggable={false}
            />
            <img
              src={encodeURI(Path.Img + item.image + Extension.Png)}
              alt=""
              draggable={false}
            />
          </picture>
        </div>
      {/each}
    </div>
    <div class="buttons">
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- 4 clickable image buttons in 2x2 grid -->
  {#each targetVerbAndObjects.value as target, i}
    <button
      data-button-index={i}
      style="background-image: url('{encodeURI(
        Path.ImgBg + target.image + Extension.ImgBg,
      )}'); 
      background-image: image-set('{encodeURI(
        Path.ImgBg + target.image + Extension.WebpBg,
      )}'); background-size: cover;"
      class={"img img" + i}
      class:img-wiggle={imgWiggle[i]}
      class:img-shadow-glow={imgWiggle[i]}
      class:img-wait={currentState !== State.WaitForResponse}
      class:img-error={currentState === State.Error && clickedItem === i}
      class:img-hidden={targetFound[i]}
      disabled={currentState !== State.WaitForResponse ||
        (currentState === State.WaitForResponse && targetFound[i])}
      onclick={() => imgClicked(i)}
    >
      <!-- #each even though only one at a time - in order to be svelte animated  -->
      {#each sendItems[i] as item (item)}
        <picture animate:flip out:send|global={{ key: item }}>
          <source
            type="image/webp"
            srcset={encodeURI(Path.Img + target.image + Extension.Webp)}
            draggable={false}
          />
          <img
            src={encodeURI(Path.Img + target.image + Extension.Png)}
            alt=""
            draggable={false}
          />
        </picture>
      {/each}
    </button>
    <div
      class={"finger-img finger-img-" + i}
      class:finger-point={fingerPointing[i]}
    >
      <picture class="finger-png">
        <source
          type="image/webp"
          srcset={encodeURI("finger-2.webp")}
          draggable={false}
        />
        <img draggable={false} src={encodeURI("finger-2.png")} alt="" />
      </picture>
    </div>
  {/each}
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ===================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 49% 49%;
    grid-template-rows: 14% 41% 41% 2%;
    grid-template-areas:
      "top top"
      "img1 img2"
      "img3 img4"
      "prog prog";
    gap: 2%;
    padding: 3%;
    padding-top: 1%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .img0 {
    grid-area: img1;
  }
  .img1 {
    grid-area: img2;
  }
  .img2 {
    grid-area: img3;
  }
  .img3 {
    grid-area: img4;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .img-hidden {
    visibility: hidden;
  }
  .finger-png {
    object-position: bottom right;
  }
  .finger-img {
    z-index: 2;
    transform: translate(-5%, 0vh);
    visibility: hidden;
    pointer-events: none;
  }
  .finger-img-0 {
    grid-area: img1;
  }
  .finger-img-1 {
    grid-area: img2;
  }
  .finger-img-2 {
    grid-area: img3;
  }
  .finger-img-3 {
    grid-area: img4;
  }
  .target-div {
    position: relative;
    margin-right: 2%;
    flex: 1 auto;
    height: calc(var(--vh, 1vh) * 12);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background-image: url("../hand-2.png");
    background-image: image-set("../hand-2.webp");
    background-repeat: no-repeat;
    background-position: right;
    background-size: contain;
  }
  .buttons {
    flex: 0 auto;
  }
  .target-img-div {
    position: absolute;
    border: solid 1px black;
    height: 24%;
    width: 8%;
    margin-right: 5%;
    object-fit: contain;
    object-position: bottom;
    box-shadow: 1vmin 1vmin 1vmin hsl(0deg 0% 0% / 0.2);
  }
  @media screen and (max-aspect-ratio: 6/5) {
    .target-img-div {
      width: 10%;
    }
  }
  @media screen and (max-aspect-ratio: 5/5) {
    .target-img-div {
      width: 14%;
    }
  }
  @media screen and (max-aspect-ratio: 4/5) {
    .target-img-div {
      width: 16%;
    }
  }
  .target-0 {
    margin-top: -7%;
    z-index: 3;
  }
  .target-1 {
    margin-top: 0;
    z-index: 2;
    transform: translateX(20%);
  }
  .target-2 {
    margin-top: 7%;
    z-index: 1;
    transform: translateX(40%);
  }
  /* finger pointing help with animated finger sliding up */
  .finger-point {
    visibility: visible;
    animation-duration: 2s;
    animation-name: slideup;
  }
  @keyframes slideup {
    from {
      transform: translate(-5%, 50vh);
    }
    to {
      transform: translate(-5%, 0vh);
    }
  }
</style>
